import { useRef, useState } from "react";
import { AlertCircle, Check, Send } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { company, mailto } from "../../data/site";
import { cx } from "../../lib/cx";
import { submitHubSpotForm } from "../../lib/hubspot";

const value = (form, name) => form.elements[name]?.value.trim() || "";

/** If HubSpot can't be reached: an email draft with what they typed. */
function draftEmail(form) {
  const field = (name) => value(form, name);
  return mailto({
    subject: `Website inquiry: ${field("company") || field("lastname")}`,
    body: `Name: ${field("firstname")} ${field("lastname")}\nCompany: ${field("company")}\nEmail: ${field("email")}\nPhone: ${field("phone")}\n\n${field("message")}`,
  });
}

// The old site's HubSpot contact form, field for field.
const fields = [
  {
    name: "firstname",
    label: "First name",
    autoComplete: "given-name",
    required: true,
    maxLength: 80,
    error: "Enter your first name.",
  },
  {
    name: "lastname",
    label: "Last name",
    autoComplete: "family-name",
    required: true,
    maxLength: 80,
    error: "Enter your last name.",
  },
  {
    name: "company",
    label: "Company",
    autoComplete: "organization",
    required: true,
    maxLength: 160,
    error: "Enter your company name.",
  },
  {
    name: "email",
    label: "Work email",
    type: "email",
    autoComplete: "email",
    required: true,
    maxLength: 200,
    error: "Enter an email address like name@company.com.",
  },
  {
    name: "phone",
    label: "Phone",
    type: "tel",
    autoComplete: "tel",
    maxLength: 40,
    wide: true,
  },
  {
    name: "message",
    label: "What can we help with?",
    multiline: true,
    required: true,
    maxLength: 5000,
    wide: true,
    error: "Tell us a little about what you need.",
  },
];

const inputClass = (invalid) =>
  cx(
    "mt-2 w-full rounded-brand border bg-white px-4 py-3 text-base text-ink transition outline-none focus:ring-4",
    invalid
      ? "border-[#b3261e] focus:border-[#b3261e] focus:ring-[#b3261e]/15"
      : "border-line-strong hover:border-subtle focus:border-blue focus:ring-blue/20",
  );

/**
 * Contact form. Submissions go to the HubSpot contact form, as on the old
 * site; if HubSpot can't be reached, visitors get an email draft instead.
 * Validation is inline: each problem is announced next to its field and
 * focus moves to the first one.
 */
export default function ContactForm() {
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [sentTo, setSentTo] = useState("");
  const formRef = useRef(null);

  function validate(form) {
    const found = {};
    for (const { name, error } of fields) {
      const input = form.elements[name];
      if (error && !input.value.trim()) found[name] = error;
      else if (error && !input.checkValidity()) found[name] = error;
    }
    return found;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const found = validate(form);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      form.elements[first].focus();
      return;
    }
    setStatus("sending");
    try {
      await submitHubSpotForm("contact", {
        firstname: value(form, "firstname"),
        lastname: value(form, "lastname"),
        "0-2/name": value(form, "company"),
        email: value(form, "email"),
        phone: value(form, "phone"),
        message: value(form, "message"),
      });
      setSentTo(value(form, "firstname"));
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  }

  if (status === "sent")
    return (
      <div role="status" className="py-6 text-center">
        <Check
          size={40}
          aria-hidden="true"
          className="mx-auto text-blue-deep"
        />
        <h3 className="font-display mt-3 text-[28px] text-ink uppercase">
          Thanks, {sentTo}. Message received.
        </h3>
        <p className="mx-auto mt-2 max-w-[440px] text-muted">
          Someone from our team will get back to you by email. In a hurry? Call
          us at{" "}
          <a
            href={company.phone.href}
            className="font-bold text-blue-deep underline underline-offset-2"
          >
            {company.phone.display}
          </a>
          .
        </p>
      </div>
    );

  // Clear a field's error as soon as it's fixed.
  function handleChange(e) {
    const { name } = e.target;
    if (!errors[name]) return;
    const next = { ...errors };
    delete next[name];
    if (!validate(formRef.current)[name]) setErrors(next);
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      onChange={handleChange}
      noValidate
    >
      <p className="mb-6 text-sm text-muted">
        Fields marked <span aria-hidden="true">*</span>
        <span className="sr-only">with an asterisk</span> are required.
      </p>
      <div className="grid gap-5 sm:grid-cols-2">
        {fields.map(
          ({ name, label, multiline, wide, error, required, ...props }) => {
            const Field = multiline ? "textarea" : "input";
            const invalid = Boolean(errors[name]);
            return (
              <div key={name} className={wide ? "sm:col-span-2" : undefined}>
                <label
                  htmlFor={`contact-${name}`}
                  className="block text-[15px] font-bold text-ink"
                >
                  {label}
                  {required && (
                    <span aria-hidden="true" className="ml-0.5 text-blue-deep">
                      *
                    </span>
                  )}
                </label>
                <Field
                  id={`contact-${name}`}
                  name={name}
                  required={required}
                  rows={multiline ? 5 : undefined}
                  aria-invalid={invalid || undefined}
                  aria-describedby={
                    invalid ? `contact-${name}-error` : undefined
                  }
                  className={cx(inputClass(invalid), multiline && "resize-y")}
                  {...props}
                />
                {invalid && (
                  <p
                    id={`contact-${name}-error`}
                    className="mt-2 flex items-center gap-1.5 text-sm font-bold text-[#b3261e]"
                  >
                    <AlertCircle size={16} aria-hidden="true" />
                    {errors[name]}
                  </p>
                )}
              </div>
            );
          },
        )}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-4">
        <Button type="submit" disabled={status === "sending"}>
          <Send size={18} aria-hidden="true" />
          {status === "sending" ? "Sending…" : "Send message"}
        </Button>
      </div>

      <div role="alert">
        {status === "failed" && (
          <p className="mt-6 flex items-start gap-3 rounded-brand border border-yellow bg-yellow-soft p-4 text-[15px] text-ink">
            <AlertCircle
              size={18}
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-yellow-ink"
            />
            <span>
              Sorry — your message didn't go through.{" "}
              <a
                href={draftEmail(formRef.current)}
                className="font-bold text-blue-deep underline underline-offset-2"
              >
                Send it by email instead
              </a>{" "}
              (we've filled it in for you), or write to{" "}
              <a
                href={`mailto:${company.email}`}
                className="font-bold text-blue-deep underline underline-offset-2"
              >
                {company.email}
              </a>
              .
            </span>
          </p>
        )}
      </div>
    </form>
  );
}
